/**
 * Centralized error messages for consistent user feedback
 */

// Firebase Authentication Errors
export const AUTH_ERRORS = {
  // Login errors
  'auth/user-not-found': 'No account found with this email address',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Incorrect email or password. Please try again.',
  'auth/invalid-email': 'Please enter a valid email address',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/too-many-requests': 'Too many failed login attempts. Please try again later or reset your password.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',

  // Signup errors
  'auth/email-already-in-use': 'An account with this email already exists',
  'auth/weak-password': 'Password is too weak. Please use a stronger password.',
  'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',

  // Password reset errors
  'auth/invalid-action-code': 'This password reset link is invalid or has expired',
  'auth/expired-action-code': 'This password reset link has expired. Please request a new one.',

  // Generic fallback
  'default': 'An error occurred. Please try again.',
} as const;

// Form Validation Error Messages
export const VALIDATION_ERRORS = {
  // Required fields
  required: (fieldName: string) => `${fieldName} is required`,

  // Email validation
  emailRequired: 'Email address is required',
  emailInvalid: 'Please enter a valid email address',
  emailFormat: 'Email must be in format: name@example.com',

  // Password validation (HIPAA compliant requirements)
  passwordRequired: 'Password is required',
  passwordTooShort: (minLength: number) => `Password must be at least ${minLength} characters`,
  passwordWeak: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character',
  passwordMismatch: 'Passwords do not match',
  passwordStrength: {
    weak: 'Weak password',
    fair: 'Fair password',
    good: 'Almost there',
    strong: 'Strong password',
  },
  passwordRequirements: 'Password must contain: 8+ characters, uppercase, lowercase, number, and special character (!@#$%^&*)',

  // Name validation
  firstNameRequired: 'First name is required',
  lastNameRequired: 'Last name is required',
  displayNameRequired: 'Display name is required',

  // Organization validation
  firmNameRequired: 'Law firm name is required',

  // Phone validation
  phoneInvalid: 'Please enter a valid phone number (e.g., (555) 123-4567)',
  faxInvalid: 'Please enter a valid fax number',

  // Address validation
  addressRequired: 'Address is required',
  cityRequired: 'City is required',
  stateRequired: 'State is required',
  stateInvalid: 'Please select a valid state',
  zipRequired: 'ZIP code is required',
  zipInvalid: 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)',

  // Client/Provider validation
  clientRequired: 'Please select a client',
  providerRequired: 'Please select at least one provider',

  // Generic validation
  invalidFormat: (fieldName: string) => `${fieldName} format is invalid`,
  tooShort: (fieldName: string, minLength: number) =>
    `${fieldName} must be at least ${minLength} characters`,
  tooLong: (fieldName: string, maxLength: number) =>
    `${fieldName} must not exceed ${maxLength} characters`,
} as const;

// API/Service Error Messages
export const API_ERRORS = {
  networkError: 'Network error. Please check your connection and try again.',
  timeout: 'Request timed out. Please try again.',
  serverError: 'Server error. Please try again later.',
  unauthorized: 'You are not authorized to perform this action.',
  notFound: 'The requested resource was not found.',
  conflict: 'This resource already exists.',

  // Generic fallbacks
  genericError: 'An unexpected error occurred. Please try again.',
  unknownError: 'Something went wrong. Please try again or contact support.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  loginSuccess: 'Login successful!',
  signupSuccess: 'Account created successfully!',
  logoutSuccess: 'Logged out successfully',
  passwordResetSent: 'Password reset email sent. Please check your inbox.',
  passwordChanged: 'Password changed successfully',
  profileUpdated: 'Profile updated successfully',

  // Generic
  saveSuccess: 'Changes saved successfully',
  deleteSuccess: 'Deleted successfully',
  createSuccess: 'Created successfully',
} as const;

/**
 * Get user-friendly error message from Firebase auth error
 */
export function getAuthErrorMessage(errorCode: string): string {
  return AUTH_ERRORS[errorCode as keyof typeof AUTH_ERRORS] || AUTH_ERRORS.default;
}

/**
 * Map Firebase error to user-friendly message
 */
export function mapFirebaseError(error: unknown): string {
  const err = error as { code?: string; message?: string };

  if (err?.code) {
    return getAuthErrorMessage(err.code);
  }

  if (err?.message) {
    return err.message;
  }

  return AUTH_ERRORS.default;
}
